import React, { FC, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import dateFormat from 'dateformat';
import {
	Avatar,
	Card,
	CardContent,
	CardHeader,
	IconButton,
	Tooltip,
	useTheme,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import CommentForm from './CommentForm';
import { Comment } from '../../lib/interfaces/Comment';
import { updateComment, replyToComment, deleteComment } from '../../api/apis';
import ContentRenderer from '../Smart/Content/ContentRenderer';
import useUserMeQuery from '../../reactQuery/hooks/useUserMeQuery';
import './CommentSingle.scss';

/* const useStyles = makeStyles(() => ({
	root: {
		maxWidth: '100%',
		marginBottom: '10px',
	},
	media: {
		height: 0,
		paddingTop: '56.25%', // 16:9
	},
	avatar: {
		// backgroundColor: secondary,
	},
})); */

type CommentSingleProps = {
	data: Comment;
	currentTaskId: number;
	parentComment?: Comment;
	refetchComments: () => void;
};

const CommentSingle: FC<CommentSingleProps> = ({
	data,
	currentTaskId,
	parentComment,
	refetchComments,
}) => {
	const [openReplyForm, setOpenReplyForm] = useState(false);
	const [openEditForm, setOpenEditForm] = useState(false);
	const { data: currentUser } = useUserMeQuery();
	const queryClient = useQueryClient();
	const theme = useTheme();
	const { t } = useTranslation();

	// const date = new Date(data.created);
	// const dateString = date.toDateString();
	// let localizedDateString = dateString.split(' ');
	// localizedDateString.shift();
	// localizedDateString[1] += ',';
	// localizedDateString = localizedDateString.join(' ');
	// if (comment.edited) {
	// localizedDateString = `${localizedDateString} (edited)`;
	// }
	const localizedDateString = dateFormat(
		data.created,
		'mmm dS, yyyy, h:MM:ss TT',
	);
	const comment = data;

	const deleteCommentMutation = useMutation<Comment, Error, Comment>(() =>
		deleteComment(comment, comment.id),
	);

	const removeComment = () => {
		deleteCommentMutation.mutate(comment, {
			onSuccess(deletedComment) {
				queryClient.setQueryData(`comments-${currentTaskId}`, (old: any) => {
					if (parentComment) {
						const modifiedQueryData = old?.map((commentItem: any) => {
							if (parentComment && commentItem.id === parentComment.id) {
								// eslint-disable-next-line no-param-reassign
								commentItem.children = commentItem.children.filter(
									(child: Comment) => child.id !== deletedComment.id,
								);
							}
							return commentItem;
						});
						return modifiedQueryData;
					}
					const modifiedQueryData = old?.filter(
						(commentItem: any) => commentItem.id !== deletedComment.id,
					);
					return modifiedQueryData;
				});
				refetchComments();
			},
		});
	};

	const openEditComment = () => {
		setOpenEditForm(true);
	};

	const closeEditForm = () => {
		setOpenEditForm(false);
	};

	const openReplyComment = () => {
		setOpenReplyForm(true);
	};

	const closeReplyForm = () => {
		setOpenReplyForm(false);
	};

	return (
		<div>
			<Card
				sx={{
					maxWidth: '100%',
					marginBottom: '10px',
					marginTop: '10px',
				}}
			>
				<CardHeader
					avatar={
						<Avatar
							aria-label="recipe"
							sx={{ background: theme.palette.secondary.main }}
							src={comment.author?.avatar || ''}
						>
							{comment.author?.avatar ? '' : comment.author?.firstName.split('')[0]}
						</Avatar>
					}
					action={
						<div>
							<Tooltip title={t('replay')}>
								<IconButton aria-label="settings" onClick={openReplyComment}>
									<ReplyIcon />
								</IconButton>
							</Tooltip>
							{comment.author.id === currentUser?.id ? (
								<Tooltip title={t('edit')}>
									<IconButton aria-label="settings" onClick={openEditComment}>
										<EditIcon />
									</IconButton>
								</Tooltip>
							) : null}
							{comment.author.id === currentUser?.id ? (
								<Tooltip title={t('delete')}>
									<IconButton aria-label="settings" onClick={removeComment}>
										<DeleteIcon />
									</IconButton>
								</Tooltip>
							) : null}
						</div>
					}
					title={comment.author?.firstName}
					subheader={localizedDateString}
				/>
				<CardContent className="Comment">
					{/* <Typography variant="body1" color="textSecondary" component="p">
						<a target="_blank" href={comment.link} rel="noreferrer">
							{comment.link}
						</a>
					</Typography> */}
					{!openEditForm && (
						<>
							{comment.link && (
								<a href={comment.link} target="_blank" rel="noopener noreferrer">
									{comment.link}
								</a>
							)}
							<ContentRenderer html={comment.comment} />
						</>
					)}
					{openEditForm && (
						<CommentForm
							currentTaskId={currentTaskId}
							initialData={comment}
							onClose={closeEditForm}
							mutationFn={updateComment}
							refetchComments={refetchComments}
							editableComment={comment}
							parentComment={parentComment}
						/>
					)}
				</CardContent>
			</Card>

			{openReplyForm && (
				<CommentForm
					currentTaskId={currentTaskId}
					parentComment={parentComment}
					initialData={{
						comment: `<strong>@${comment?.author.firstName}</strong>, `,
					}}
					replyComment={comment}
					onClose={closeReplyForm}
					mutationFn={replyToComment}
					refetchComments={refetchComments}
				/>
			)}
		</div>
	);
};

export default CommentSingle;
